import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { useAuth } from '../../contexts/AuthContext';
import { usePremiumAccess } from '../../hooks/usePremiumAccess';
import { useStripe } from '../../hooks/useStripe';
import { saveProblemReport } from '../../lib/accountNotes';
import { inviteCodeFor, inviteLink, inviteShareText, type DailyMinutes } from '../../lib/onboardingModel';
import { loadRecord, publishInvite, saveRecord } from '../../lib/onboardingStore';
import { ProviderIcon } from '../onboarding/GuideBubble';
import { supabase } from '../../lib/supabaseClient';
import { SITE_CONTACT_EMAIL, SITE_OPERATOR } from '../../lib/siteOperator';
import { APP_VERSION, planLabel, STREAK_GOALS, type StreakGoal } from '../../lib/studyStats';
import { readStudyStats, setNotificationsOn, setStreakGoal } from '../../lib/studyStatsStore';

const SUPPORT_EMAIL = SITE_CONTACT_EMAIL;

function accountProvider(user: User | null): 'apple' | 'google' | 'facebook' | 'email' | null {
  if (!user) return null;
  const raw = String(user.app_metadata?.provider || user.identities?.[0]?.provider || 'email').toLowerCase();
  if (raw === 'apple') return 'apple';
  if (raw === 'google') return 'google';
  if (raw === 'facebook') return 'facebook';
  return 'email';
}

export default function SettingsPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const premium = usePremiumAccess();
  const { createPortalSession } = useStripe();
  const record = loadRecord();
  const [minutes, setMinutes] = useState<DailyMinutes | null>(record?.dailyMinutes ?? null);
  const [stats, setStats] = useState(() => readStudyStats());
  const [permission, setPermission] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [reportKind, setReportKind] = useState<'account_billing' | 'other'>('other');
  const [reportDetails, setReportDetails] = useState('');
  const [reportNote, setReportNote] = useState('');
  const [inviteNote, setInviteNote] = useState('');
  const email = user?.email || 'No account on this device';
  const provider = accountProvider(user);
  const inviteLinkText = record ? inviteLink(window.location.origin, inviteCodeFor(record.guestKey)) : '';
  const plan = premium.loading
    ? 'Checking plan…'
    : planLabel({
        hasPremiumAccess: premium.hasPremiumAccess,
        premiumTier: premium.premiumTier,
        subscriptionStatus: premium.subscriptionStatus,
      });

  const chooseMinutes = (value: DailyMinutes) => {
    setMinutes(value);
    const current = loadRecord();
    if (!current) return;
    saveRecord({ ...current, dailyMinutes: value });
  };

  const chooseGoal = (goal: StreakGoal | null) => {
    setStats(setStreakGoal(goal));
  };

  const toggleNotifications = async () => {
    const next = !stats.notificationsOn;
    if (next && typeof Notification !== 'undefined') {
      try {
        const result = await Notification.requestPermission();
        setPermission(result);
      } catch {
        setPermission('denied');
      }
    }
    setStats(setNotificationsOn(next));
  };

  const sendReport = async () => {
    const result = await saveProblemReport({ userId: user?.id ?? null, kind: reportKind, details: reportDetails });
    if (result === 'invalid') {
      setReportNote('Write a short note first.');
      return;
    }
    setReportNote(result === 'saved' ? 'Saved.' : 'Saved on this device.');
    setReportDetails('');
  };

  const shareInvite = async () => {
    const current = loadRecord();
    if (!current) {
      setInviteNote('Start a path before sharing an invite.');
      return;
    }
    const published = await publishInvite(current);
    const link = inviteLink(window.location.origin, inviteCodeFor(published.guestKey));
    const text = inviteShareText(link);
    try {
      if (navigator.share) {
        await navigator.share({ text, url: link });
        setInviteNote(link);
        return;
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
    }
    try {
      await navigator.clipboard.writeText(`${text}`);
      setInviteNote(link);
    } catch {
      setInviteNote(link);
    }
  };

  const deleteAccount = async () => {
    if (!user) return;
    const { error } = await supabase.rpc('delete_own_account');
    if (error) {
      setDeleteError(error.message);
      return;
    }
    await signOut();
  };

  return (
    <div className="font-myanmar mx-auto w-full max-w-lg min-w-0 overflow-x-hidden px-4 py-6" data-testid="settings-page">
      <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
      <p className="lesson-subtext text-sm text-gray-600">ဆက်တင်</p>

      <section className="mt-6 rounded-2xl bg-white px-4 py-4">
        <h2 className="text-base font-semibold">Account</h2>
        <div className="mt-1 flex items-center gap-2">
          {provider ? <ProviderIcon provider={provider} /> : null}
          <p className="min-w-0 break-all text-sm" data-testid="settings-email">{email}</p>
        </div>
        <p className="mt-3 text-sm font-semibold">Delete account</p>
        <p className="text-xs text-gray-600">Required by Apple. Lessons on this device stay on this device.</p>
        {!user ? <p className="mt-2 text-sm" data-testid="settings-delete">Sign in to delete an account.</p> : null}
        {user && !confirming ? (
          <button type="button" className="mt-3 min-h-[48px] w-full rounded-2xl border border-red-700 text-sm font-semibold text-red-800" data-testid="settings-delete" onClick={() => setConfirming(true)}>
            Delete account
          </button>
        ) : null}
        {user && confirming ? (
          <div className="mt-3">
            <p className="text-sm">This deletes the account for {email}.</p>
            <button type="button" className="mt-2 min-h-[48px] w-full rounded-2xl bg-red-700 text-sm font-semibold text-white" data-testid="settings-delete-confirm" onClick={() => void deleteAccount()}>
              Delete this account
            </button>
          </div>
        ) : null}
        {deleteError ? (
          <p className="mt-2 break-words text-sm text-red-800" data-testid="settings-delete-error">
            {deleteError} Email <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        ) : null}
      </section>

      <section className="mt-4 rounded-2xl bg-white px-4 py-4">
        <h2 className="text-base font-semibold">Manage subscription</h2>
        <p className="mt-1 text-sm" data-testid="settings-plan">Current plan: {plan}</p>
        <button
          type="button"
          className="mt-3 min-h-[48px] w-full rounded-2xl border border-gray-300 text-sm font-semibold"
          data-testid="settings-restore"
          onClick={() => {
            if (!user) {
              navigate('/auth?mode=login');
              return;
            }
            void createPortalSession();
          }}
        >
          Restore purchase
        </button>
        <a className="mt-3 inline-flex min-h-[44px] items-center text-sm font-semibold text-teal-800" data-testid="settings-support" href={`mailto:${SUPPORT_EMAIL}`}>
          {SITE_OPERATOR} · {SUPPORT_EMAIL}
        </a>
      </section>

      <section className="mt-4 rounded-2xl bg-white px-4 py-4">
        <h2 className="text-base font-semibold">Goal settings</h2>
        <p className="mt-1 text-xs text-gray-600">Daily minutes</p>
        <div className="mt-2 flex gap-2">
          {([2, 5, 10] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={`min-h-[44px] flex-1 rounded-full text-sm font-semibold ${minutes === value ? 'bg-teal-700 text-white' : 'bg-stone-100'}`}
              data-testid={`settings-goal-${value}`}
              aria-pressed={minutes === value}
              onClick={() => chooseMinutes(value)}
            >
              {value}
            </button>
          ))}
        </div>
        {!record ? <p className="mt-2 text-xs text-gray-600">Start a path to keep the daily minutes.</p> : null}
        <p className="mt-4 text-xs text-gray-600">Streak goal</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {STREAK_GOALS.map((goal) => (
            <button
              key={goal}
              type="button"
              className={`min-h-[44px] rounded-full px-4 text-sm font-semibold ${stats.streakGoal === goal ? 'bg-teal-700 text-white' : 'bg-stone-100'}`}
              data-testid={`settings-streak-${goal}`}
              aria-pressed={stats.streakGoal === goal}
              onClick={() => chooseGoal(goal)}
            >
              {goal}
            </button>
          ))}
        </div>
      </section>

      <section className="mt-4 rounded-2xl bg-white px-4 py-4">
        <h2 className="text-base font-semibold">Notifications</h2>
        <button
          type="button"
          className="mt-2 min-h-[48px] w-full rounded-2xl border border-gray-300 text-sm font-semibold"
          data-testid="settings-notifications"
          aria-pressed={stats.notificationsOn}
          onClick={() => void toggleNotifications()}
        >
          {stats.notificationsOn ? 'On' : 'Off'}
        </button>
        <p className="mt-2 text-sm text-gray-600">Turning this on asks the browser. It does not deliver a reminder while the app is closed.</p>
        {permission ? <p className="mt-1 text-sm" data-testid="settings-permission">{permission}</p> : null}
      </section>

      <section className="mt-4 rounded-2xl bg-white px-4 py-4">
        <h2 className="text-base font-semibold">Report a problem</h2>
        <div className="mt-2 grid gap-2">
          <label className="flex min-h-[44px] items-center gap-2 text-sm">
            <input type="radio" name="problem-kind" checked={reportKind === 'account_billing'} onChange={() => setReportKind('account_billing')} data-testid="settings-report-billing" />
            Account or billing
          </label>
          <label className="flex min-h-[44px] items-center gap-2 text-sm">
            <input type="radio" name="problem-kind" checked={reportKind === 'other'} onChange={() => setReportKind('other')} data-testid="settings-report-other" />
            Other
          </label>
        </div>
        <textarea
          className="mt-2 min-h-[96px] w-full rounded-2xl border border-gray-300 px-3 py-2 text-sm"
          data-testid="settings-report-details"
          value={reportDetails}
          maxLength={800}
          onChange={(event) => setReportDetails(event.target.value)}
        />
        <button type="button" className="mt-2 min-h-[48px] w-full rounded-2xl bg-teal-700 text-sm font-semibold text-white" data-testid="settings-report-send" onClick={() => void sendReport()}>
          Send
        </button>
        {reportNote ? <p className="mt-2 text-sm" data-testid="settings-report-note">{reportNote}</p> : null}
      </section>

      <section className="mt-4 rounded-2xl bg-white px-4 py-4">
        <h2 className="text-base font-semibold">Invite a friend</h2>
        <button type="button" className="mt-2 min-h-[48px] w-full rounded-2xl border border-gray-300 text-sm font-semibold" data-testid="settings-invite" onClick={() => void shareInvite()}>
          Share invite
        </button>
        {inviteLinkText ? <p className="mt-2 break-all text-sm" data-testid="settings-invite-link">{inviteLinkText}</p> : null}
        {inviteNote ? <p className="mt-2 break-all text-sm">{inviteNote}</p> : null}
      </section>

      <section className="mt-4 rounded-2xl bg-white px-4 py-4">
        {user ? (
          <button type="button" className="min-h-[48px] w-full rounded-2xl border border-gray-300 text-sm font-semibold" data-testid="settings-logout" onClick={() => void signOut()}>
            Log out
          </button>
        ) : (
          <p className="text-sm" data-testid="settings-logout">You are not signed in.</p>
        )}
        <p className="mt-3 text-xs text-gray-500" data-testid="settings-version">Version {APP_VERSION}</p>
      </section>
    </div>
  );
}
